<template>
    <div>

        <ListDialog v-if="showListDialog" v-bind="listDialogParameters"
                    v-on:hidden="showListDialog = false"/>

        <RecordDialog v-if="showRecordDialog" v-bind="recordDialogParameters"
                      v-on:hidden="showRecordDialog = false"
                      v-on:model-created="onModelCreated"
                      v-on:ok="fetchCrud"/>

        <div class="card st-framework-card">

            <div class="card-header">
                <table style="border-collapse: collapse; width: 100%;">
                    <tr>
                        <td style="margin: 0; padding: 0 0 0 1rem; width: 50px;">
                            <font-awesome-icon :icon="icon" size="sm"/>
                        </td>
                        <td style="text-align: center;">
                            {{ $i18n.tc('translations.' + name, 2) }}
                        </td>
                        <td style="width: 50px;"/>
                    </tr>
                </table>
            </div>

            <div class="card-body" v-if="pageLoaded">

                <!-- FILTER -->

                <div class="input-group input-group-sm mb-3">
                    <div class="input-group-prepend input-group-text">
                        <font-awesome-icon :icon="['fas', 'search']" size="sm"/>
                    </div>
                    <input id="filter" ref="filter" type="search" class="form-control" :placeholder="$i18n.tc('translations.'+filterPlaceholder,1)"
                           v-model="pageInfo.filter"
                           v-on:input="debounceInput"/>
                    <div class="input-group-append">
                        <template v-for="(action,index) in actions">
                            <template v-if="action.placement === 'FILTERRIGHT'">
                                <ButtonAction :key="action.name + '-' + index" :action="action"
                                              v-on:create-row="createModel"
                                              v-if="action.type === 'BUTTON'"/>
                                <RouteAction :key="action.name + '-' + index" :action="action"
                                             :data="data"
                                             v-if="action.type === 'ROUTE'"/>
                                <SelectAction :key="action.name + '-' + index" :action="action"
                                              v-on:filter-option="onFilterOption(action, $event)"
                                              v-if="action.type === 'SELECT'"/>
                                <ToggleAction :key="action.name + '-' + index" :action="action"
                                              v-on:filter-option="onFilterToggle(action, $event)"
                                              v-if="action.type === 'TOGGLE'"/>
                            </template>
                        </template>
                    </div>
                </div>

                <!-- TABLE -->

                <CrudTable :key="'crudtable-'+crudReload" :data="data" :fields="fields" :actions="actions" :pageInfo="pageInfo" :state="state"
                           v-on:delete-row="deleteModel"
                           v-on:detail-row="detailModel"
                           v-on:download-event="download"
                           v-on:inline-edit="fetchCrud"
                           v-on:list-row="listModel"
                           v-on:restore-row="restoreModel"
                           v-on:selection-event="selectionEvent"
                           v-on:selective-update-row="selectiveUpdateModel"
                           v-on:toggle-sort="toggleSort"
                           v-on:toggle-stateintable="toggleStateInTable"
                           v-on:update-row="updateModel"/>

                <!-- PAGINATE -->

                <ul class="pagination justify-content-center m-1" v-if="meta.last_page > 1">
                    <li class="page-item" :class="{'disabled': meta.current_page === 1}">
                        <button class="page-link" v-on:click="changePage(links.first)">
                            <font-awesome-icon :icon="['fas', 'angle-double-left']" size="sm"/>
                        </button>
                    </li>
                    <li class="page-item" :class="{'disabled': links.prev === null}">
                        <button class="page-link" v-on:click="changePage(links.prev)">
                            <font-awesome-icon :icon="['fas', 'angle-left']" size="sm"/>
                        </button>
                    </li>
                    <li class="page-item disabled">
                        <a class="page-link">
                            {{ meta.current_page }} {{ $i18n.t('translations.of') }} {{ meta.last_page }}
                            ({{ meta.total }})
                        </a>
                    </li>
                    <li class="page-item" :class="{'disabled': links.next === null}">
                        <button class="page-link" v-on:click="changePage(links.next)">
                            <font-awesome-icon :icon="['fas', 'angle-right']" size="sm"/>
                        </button>
                    </li>
                    <li class="page-item" :class="{'disabled': meta.current_page === meta.last_page}">
                        <button class="page-link" v-on:click="changePage(links.last)">
                            <font-awesome-icon :icon="['fas', 'angle-double-right']" size="sm"/>
                        </button>
                    </li>
                </ul>

            </div>
        </div>

    </div>
</template>

<script>
import * as _ from "debounce";
import ButtonAction from "@/components/actions/ButtonAction.vue";
import CrudTable from "../components/crud/CrudTable";
import FileDownload from "@/mixins/FileDownload";
import ListDialog from "@/components/dialogs/ListDialog";
import RecordDialog from "@/components/dialogs/RecordDialog";
import RouteAction from "@/components/actions/RouteAction.vue";
import SelectAction from "@/components/actions/SelectAction.vue";
import ToggleAction from "@/components/actions/ToggleAction.vue";

export default {
    name: "Crud",
    components: {
        ButtonAction,
        CrudTable,
        ListDialog,
        RecordDialog,
        RouteAction,
        SelectAction,
        ToggleAction,
    },
    props: ['name', 'icon', 'api', 'filterPlaceholder', 'state', 'userLanguage', 'languages'],
    mixins: [FileDownload],
    data() {
        return {
            pageLoaded: false,
            crudReload: 0,
            fetchCrudId: 0,
            routeName: null,
            pageInfo: {
                current_page_link: '',
                sort_by: '',
                sort_type: '',
                filter: '',
                filter_options: {},
                stateintable: {},
            },
            data: [],
            links: [],
            meta: [],
            fields: [],
            actions: [],
            showListDialog: false,
            listDialogParameters: {
                icon: ['fas', 'user-plus'],
                title: null,
                api: null,
            },
            showRecordDialog: false,
            recordDialogParameters: {
                type: null,
                title: null,
                api: null,
                ok_event: null,
                fields: null,
                data: null,
                languages: this.languages,
                userLanguage: this.userLanguage,
            },
        }
    },
    watch: {
        name: function () {
            this.initialize();
        }
    },
    created() {
        this.initialize();
    },
    methods: {
        changePage(link) {
            this.pageInfo.current_page_link = link;
            this.fetchCrud();
        },
        createModel(action) {
            this.recordDialogParameters.type = "create";
            this.recordDialogParameters.title = this.$i18n.tc("translations." + this.name, 1);
            this.recordDialogParameters.api = this.api;
            if (action.routename) {
                this.routeName = action.routename;
                this.recordDialogParameters.ok_event = "model-created";
            } else {
                this.recordDialogParameters.ok_event = "ok";
            }
            this.recordDialogParameters.fields = this.fields;
            this.recordDialogParameters.data = null;
            this.showRecordDialog = true;
        },
        debounceInput: _.debounce(function () {
            this.fetchCrud();
        }, 450),
        deleteModel(row) {
            this.recordDialogParameters.type = "delete";
            this.recordDialogParameters.title = row.label;
            this.recordDialogParameters.api = this.api;
            this.recordDialogParameters.ok_event = "ok";
            this.recordDialogParameters.fields = this.fields;
            this.recordDialogParameters.data = row;
            this.showRecordDialog = true;
        },
        detailModel(row) {
            this.recordDialogParameters.type = "detail";
            this.recordDialogParameters.title = row.label + ' (#' + row.id + ')';
            this.recordDialogParameters.api = this.api;
            this.recordDialogParameters.ok_event = "ok";
            this.recordDialogParameters.fields = this.fields;
            this.recordDialogParameters.data = row;
            this.showRecordDialog = true;
        },
        download(choice) {
            this.state.loading = true;
            this.$http.post(choice.api, {
                'writer_type': choice.writer_type,
                'filter': this.pageInfo.filter,
                'filter_options': this.pageInfo.filter_options,
                'stateintable': this.pageInfo.stateintable,
                'ids': choice.ids,
            }, {'responseType': 'blob'}).then((res) => {
                this.fileDownload(choice, res.data);
                this.state.loading = false;
            }).catch((error) => {
                console.log("Crud:download():error:", error);
            });
        },
        fetchCrud() {
            this.state.loading = true;
            let currentId = ++this.fetchCrudId;
            this.$http.post(this.pageInfo.current_page_link, this.pageInfo, {}).then((res) => {
                if (currentId === this.fetchCrudId) {
                    let tmp = res.data.data;
                    tmp.filter(record => record.selected = false);
                    this.data = tmp;
                    this.actions = res.data.actions;
                    this.fields = res.data.fields;
                    this.links = res.data.links;
                    this.meta = res.data.meta;
                    this.pageInfo.sort_by = res.data.sort_by;
                    this.pageInfo.sort_type = res.data.sort_type;
                    this.pageInfo.current_page_link = this.meta.path;
                    this.crudReload++;
                    this.pageLoaded = true;
                    this.state.loading = false;
                    this.$nextTick(() => {
                        if (this.$refs.filter) {
                            this.$refs.filter.focus();
                        }
                    });
                }
            }).catch((error) => {
                console.log("Crud:fetchCrud():error:", error);
            });
        },
        initialize() {
            this.pageInfo = {
                current_page_link: this.api + '/crud',
                sort_by: '',
                sort_type: '',
                filter: '',
                filter_options: {},
                stateintable: {},
            };
            this.fetchCrud();
        },
        listModel(data) {
            if (this.name === 'Role') {
                this.listDialogParameters.title = this.$i18n.t("translations.Assign Permissions to Role: {role}", {role: data.name});
                this.listDialogParameters.api = this.api + '/permissions/' + data.id;
            } else {
                this.listDialogParameters.title = this.$i18n.t("translations.Assign Roles to User: {user}", {user: data.name});
                this.listDialogParameters.api = this.api + '/roles/' + data.id;
            }
            this.showListDialog = true;
        },
        onFilterOption(action, option) {
            this.pageInfo.filter_options[action.name] = option;
            this.fetchCrud();
        },
        onFilterToggle(action) {
            this.pageInfo.filter_options[action.name] = action.toggled;
            this.fetchCrud();
        },
        onModelCreated(data) {
            this.$router.push({name: this.routeName, params: {id: data.data.id}});
        },
        restoreModel(row) {
            this.recordDialogParameters.type = "restore";
            this.recordDialogParameters.title = row.label;
            this.recordDialogParameters.api = this.api;
            this.recordDialogParameters.ok_event = "ok";
            this.recordDialogParameters.fields = this.fields;
            this.recordDialogParameters.data = row;
            this.showRecordDialog = true;
        },
        selectionEvent(choice) {
            this.state.loading = true;
            this.$http.post(choice.api, {
                ...(choice.writer_type && {'writer_type': choice.writer_type}),
                'ids': choice.ids
            }, {
                ...(choice.filename && {'responseType': 'blob'}),
            }).then((res) => {
                if (choice.filename) {
                    this.fileDownload(choice, res.data);
                }
                this.state.loading = false;
            }).catch((error) => {
                console.log("Crud:selectionEvent():error:", error);
            });
        },
        selectiveUpdateModel(row) {
            this.state.loading = true;
            this.$http.get(this.api + '/crud_model_fields/' + row.id).then((res) => {
                this.recordDialogParameters.type = "update";
                this.recordDialogParameters.title = row.label;
                this.recordDialogParameters.api = this.api;
                this.recordDialogParameters.ok_event = "ok";
                this.recordDialogParameters.fields = res.data.fields;
                this.recordDialogParameters.data = row;
                this.showRecordDialog = true;
                this.state.loading = false;
            }).catch((error) => {
                console.log("Crud:selectiveUpdateModel():error:", error);
            });
        },
        toggleSort(name) {
            if (this.pageInfo.sort_by === name) {
                if (this.pageInfo.sort_type === 'asc') {
                    this.pageInfo.sort_type = 'desc';

                } else {
                    this.pageInfo.sort_type = 'asc';
                }
            } else {
                this.pageInfo.sort_by = name;
                this.pageInfo.sort_type = 'asc';
            }
            this.fetchCrud();
        },
        toggleStateInTable(field) {
            this.pageInfo.stateintable[field.name] = field.stateintable;
            this.fetchCrud();
        },
        updateModel(row) {
            this.recordDialogParameters.type = "update";
            this.recordDialogParameters.title = row.label;
            this.recordDialogParameters.api = this.api;
            this.recordDialogParameters.ok_event = "ok";
            this.recordDialogParameters.fields = this.fields;
            this.recordDialogParameters.data = row;
            this.showRecordDialog = true;
        },
    }
}
</script>

<style scoped>
</style>
